import React, { useState, useEffect } from 'react';
import { Container, Form, Row, Col, Button, Alert, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useInterfaceStore } from '../../stores/useInterfaceStore';
import { notify } from '../../stores/useNotificationStore';
import SearchSelectField, { SearchOption } from '../../components/SearchSelectField';

interface IPessoa {
  id: number;
  nome: string;
}

interface IFormData {
  nome: string;
  email: string;
  telefone: string;
  funcao: string;
  departamento: string;
  diretoria_nome: string;
  gestor_id: string;
  tipo_pessoa: string;
  senha: string;
  pessoa_id: string;
}

const initialState: IFormData = {
  nome: '',
  email: '',
  telefone: '',
  funcao: '',
  departamento: '',
  diretoria_nome: '',
  gestor_id: '',
  tipo_pessoa: '',
  senha: '',
  pessoa_id: '',
};

const TIPOS_PESSOA = [
  { value: 'Administrador', label: 'Administrador' },
  { value: 'DPO', label: 'DPO' },
  { value: 'USUARIO', label: 'Executor' },
];

const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d{1,4})$/, '$1-$2');
  }
  return digits
    .replace(/^(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d{1,4})$/, '$1-$2');
};

const isValidEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const UserRegistration: React.FC = () => {
  const [formData, setFormData] = useState<IFormData>(initialState);
  const [pessoas, setPessoas] = useState<IPessoa[]>([]);
  const [deptOptions, setDeptOptions] = useState<SearchOption[]>([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { empresaId } = useInterfaceStore();

  // Carrega pessoas (Gestor Imediato) e o Cadastro Auxiliar de Departamentos — a mesma
  // fonte usada no Cadastro de Pessoa, para que um departamento herdado ao vincular uma
  // Pessoa (ex.: "RECURSOS HUMANOS") apareça corretamente, mesmo fora da lista fixa antiga.
  useEffect(() => {
    const loadSelects = async () => {
      setIsLoadingData(true);
      try {
        const [pessoasRes, deptRes] = await Promise.all([
          api.getPessoas(empresaId ?? undefined),
          api.getCadastrosAuxiliares('departamento', empresaId ?? undefined),
        ]);
        if (pessoasRes.success && pessoasRes.data) setPessoas(pessoasRes.data as IPessoa[]);
        if (deptRes.success && Array.isArray(deptRes.data)) {
          setDeptOptions((deptRes.data as any[]).filter(d => d.ativo !== false).map(d => ({ key: d.nome, label: d.nome })));
        }
      } catch (err) {
        console.error('Erro ao carregar dados do formulário:', err);
      } finally {
        setIsLoadingData(false);
      }
    };
    loadSelects();
  }, [empresaId]);

  // Cadastra rapidamente um novo Departamento no Cadastro Auxiliar (botão "+").
  const criarDepartamento = async (nome: string): Promise<SearchOption | null> => {
    if (!empresaId) { notify.warning('Nenhuma empresa selecionada.'); return null; }
    try {
      const res = await api.createCadastroAuxiliar({ tipo: 'departamento', nome, empresa_id: empresaId });
      if (res.success && res.data) {
        const opt: SearchOption = { key: nome, label: nome };
        setDeptOptions(prev => [...prev, opt]);
        notify.success('Cadastrado com sucesso!');
        return opt;
      }
      notify.error((res.error as string) || 'Erro ao cadastrar.');
      return null;
    } catch {
      notify.error('Erro de conexão ao cadastrar.');
      return null;
    }
  };

  // Carrega dados do usuário ao editar
  useEffect(() => {
    if (!id) return;
    const loadUser = async () => {
      setIsLoading(true);
      try {
        const response = await api.getUser(parseInt(id));
        if (response.success && response.data) {
          const u = response.data as any;
          setFormData({
            nome: u.nome || '',
            email: u.email || '',
            telefone: u.telefone || '',
            funcao: u.funcao || '',
            departamento: u.departamento || '',
            diretoria_nome: u.diretoria_nome || '',
            gestor_id: u.gestor_id ? String(u.gestor_id) : '',
            tipo_pessoa: u.tipo_pessoa || '',
            senha: '',
            pessoa_id: u.pessoa_id ? String(u.pessoa_id) : '',
          });
        } else {
          setError('Usuário não encontrado.');
          navigate('/app/usuarios/list');
        }
      } catch {
        setError('Erro ao carregar dados do usuário.');
      } finally {
        setIsLoading(false);
      }
    };
    loadUser();
  }, [id, navigate]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'telefone') {
      setFormData(prev => ({ ...prev, telefone: formatPhone(value) }));
    } else if (name === 'pessoa_id' && value) {
      // Ao selecionar uma Pessoa, pré-preenche campos do formulário
      const pessoa = pessoas.find(p => String(p.id) === value) as any;
      if (pessoa) {
        setFormData(prev => ({
          ...prev,
          pessoa_id: value,
          nome: pessoa.nome || prev.nome,
          email: pessoa.email || prev.email,
          telefone: pessoa.telefone ? formatPhone(pessoa.telefone) : prev.telefone,
          funcao: pessoa.funcao || prev.funcao,
          departamento: pessoa.departamento || prev.departamento,
          diretoria_nome: pessoa.diretoria || prev.diretoria_nome,
          gestor_id: pessoa.gestor_id ? String(pessoa.gestor_id) : prev.gestor_id,
        }));
      } else {
        setFormData(prev => ({ ...prev, pessoa_id: value }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const validate = (): boolean => {
    if (!formData.nome.trim()) { setError('Nome é obrigatório.'); return false; }
    if (!formData.email.trim()) { setError('E-mail é obrigatório.'); return false; }
    if (!isValidEmail(formData.email)) { setError('E-mail inválido.'); return false; }
    if (!formData.tipo_pessoa) { setError('Perfil de acesso é obrigatório.'); return false; }
    if (!id && !formData.senha) { setError('Senha é obrigatória para novo usuário.'); return false; }
    if (!empresaId) { setError('Nenhuma empresa selecionada.'); return false; }
    return true;
  };

  const handleSubmit = async () => {
    setError('');
    if (!validate()) return;

    setIsLoading(true);
    try {
      const payload: any = {
        empresa_id: empresaId,
        nome: formData.nome,
        email: formData.email,
        telefone: formData.telefone,
        departamento: formData.departamento,
        funcao: formData.funcao,
        tipo_pessoa: formData.tipo_pessoa,
        ...(formData.pessoa_id ? { pessoa_id: parseInt(formData.pessoa_id) } : {}),
        ...(formData.senha ? { senha: formData.senha } : {}),
      };

      const response = id
        ? await api.updateUser(parseInt(id), payload)
        : await api.createUser(payload);

      if (response.success) {
        notify.success(id ? 'Usuário atualizado com sucesso!' : 'Usuário criado com sucesso!');
        navigate('/app/usuarios/list');
      } else {
        const errMsg = (response as any).data?.error || response.error || '';
        if (errMsg.includes('Email já em uso')) {
          setError('Este endereço de e-mail já está em uso.');
        } else {
          setError(errMsg || 'Erro ao salvar usuário.');
        }
      }
    } catch {
      setError('Erro de conexão ao tentar salvar o usuário.');
    } finally {
      setIsLoading(false);
    }
  };

  const emailInvalid = !!formData.email && !isValidEmail(formData.email);

  if (isLoading && id) {
    return (
      <Container fluid className="p-4 text-center">
        <Spinner animation="border" /> <span>Carregando...</span>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      <h1 className="mb-4">{id ? 'Editar Usuário' : 'Cadastro de Usuário'}</h1>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      <Form>
        <Row className="g-3">

          {/* Vincular a Pessoa existente (opcional) */}
          <Col md={12}>
            <Form.Group controlId="pessoa_id">
              <Form.Label>Vincular a Pessoa <span className="text-muted fw-normal">(opcional — pré-preenche os campos)</span></Form.Label>
              <Form.Select
                name="pessoa_id"
                value={formData.pessoa_id}
                onChange={handleChange}
                disabled={isLoadingData}
              >
                <option value="">Selecione uma pessoa cadastrada...</option>
                {pessoas.map(p => (
                  <option key={p.id} value={String(p.id)}>{p.nome}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          {/* Perfil de Acesso */}
          <Col md={6}>
            <Form.Group controlId="tipo_pessoa">
              <Form.Label>Perfil de Acesso *</Form.Label>
              <Form.Select name="tipo_pessoa" value={formData.tipo_pessoa} onChange={handleChange}>
                <option value="">Selecione</option>
                {TIPOS_PESSOA.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          {/* Senha */}
          <Col md={6}>
            <Form.Group controlId="senha">
              <Form.Label>Senha {!id && '*'}</Form.Label>
              <Form.Control
                type="password"
                name="senha"
                value={formData.senha}
                onChange={handleChange}
                placeholder={id ? 'Deixe em branco para manter a senha atual' : ''}
              />
            </Form.Group>
          </Col>

          {/* Nome */}
          <Col md={6}>
            <Form.Group controlId="nome">
              <Form.Label>Nome *</Form.Label>
              <Form.Control
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>

          {/* E-mail */}
          <Col md={6}>
            <Form.Group controlId="email">
              <Form.Label>E-mail *</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                isInvalid={emailInvalid}
              />
              <Form.Control.Feedback type="invalid">E-mail inválido.</Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Telefone */}
          <Col md={6}>
            <Form.Group controlId="telefone">
              <Form.Label>Telefone</Form.Label>
              <Form.Control
                type="tel"
                name="telefone"
                value={formData.telefone}
                onChange={handleChange}
                placeholder="(00) 00000-0000"
              />
            </Form.Group>
          </Col>

          {/* Função */}
          <Col md={6}>
            <Form.Group controlId="funcao">
              <Form.Label>Função</Form.Label>
              <Form.Control
                type="text"
                name="funcao"
                value={formData.funcao}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>

          {/* Departamento (Cadastro Auxiliar: departamento) */}
          <Col md={6}>
            <Form.Group controlId="departamento">
              <Form.Label>Departamento</Form.Label>
              <SearchSelectField
                placeholder="Pesquisar no cadastro de Departamentos"
                options={deptOptions}
                value={formData.departamento}
                allowFreeText
                onSelect={opt => setFormData(prev => ({ ...prev, departamento: opt?.key || '' }))}
                onAdd={criarDepartamento}
              />
            </Form.Group>
          </Col>

          {/* Diretoria */}
          <Col md={6}>
            <Form.Group controlId="diretoria_nome">
              <Form.Label>Diretoria</Form.Label>
              <Form.Control
                type="text"
                name="diretoria_nome"
                value={formData.diretoria_nome}
                onChange={handleChange}
                placeholder="Nome da diretoria"
              />
            </Form.Group>
          </Col>

          {/* Gestor Imediato */}
          <Col md={6}>
            <Form.Group controlId="gestor_id">
              <Form.Label>Gestor Imediato</Form.Label>
              <Form.Select
                name="gestor_id"
                value={formData.gestor_id}
                onChange={handleChange}
                disabled={isLoadingData}
              >
                <option value="">Selecione</option>
                {pessoas
                  .filter(p => !id || String(p.id) !== formData.pessoa_id)
                  .map(p => (
                    <option key={p.id} value={String(p.id)}>{p.nome}</option>
                  ))}
              </Form.Select>
            </Form.Group>
          </Col>

        </Row>

        <div className="text-end mt-4">
          <Button
            variant="secondary"
            className="me-2"
            onClick={() => navigate('/app/usuarios/list')}
          >
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? <Spinner as="span" animation="border" size="sm" /> : 'Salvar'}
          </Button>
        </div>
      </Form>
    </Container>
  );
};

export default UserRegistration;
